import React, { useEffect, useState } from "react";
import axios from "axios";
import { Route as RouteIcon } from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";
import LoadingState from "./LoadingState";
import Pagination from "./Pagination";

const PAGE_SIZE = 25;

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
// 90 days back -- matches the backend's own default (fleet.lane_history_lookback_days)
// when no start/end is passed, kept explicit here so the date pickers show
// the real range being queried instead of looking blank on first load.
function ninetyDaysAgoIso() {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
}

const fmtMoney = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const fmtRate = (n) => (n == null ? "—" : `$${n.toFixed(2)}`);
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

// Binary good/bad vs. the fleet average -- same convention every other
// admin ranked table (AdminFleetOverview's per-dispatcher table) already
// uses, so a thin/negative-margin lane reads the same way a below-average
// dispatcher does elsewhere in this app.
function cellColor(value, avg, higherIsBetter = true) {
  if (value == null || avg == null) return undefined;
  const good = higherIsBetter ? value >= avg : value <= avg;
  return good ? "#16a34a" : "#dc2626";
}

// Answers "which lanes are actually worth chasing" -- real revenue/profit
// per lane (origin -> destination), not per truck or per dispatcher, which
// is what every other report in this app groups by instead. Deliberately
// fleet-wide for every caller, dispatcher or admin -- unlike Route History/
// HOS Compliance/Idle Miles (about MY trucks, correctly scoped), lane
// profitability is market intel a dispatcher needs to see across the whole
// fleet to decide where to send a truck next, not just their own history.
export default function LaneProfitability() {
  const [start, setStart] = useState(ninetyDaysAgoIso());
  const [end, setEnd] = useState(todayIso());
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${apiBaseUrl}/scripted/fleet/lane-profitability`, {
        ...authHeaders(),
        params: { start, end, page, page_size: PAGE_SIZE },
      })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [start, end, page]);

  // A date-range change should always land back on page 1 -- staying on
  // page 4 of a now-different, likely-shorter result set would just show
  // an empty page instead of the new data.
  function handleStartChange(v) {
    setStart(v);
    setPage(1);
  }
  function handleEndChange(v) {
    setEnd(v);
    setPage(1);
  }

  const lanes = data?.lanes || [];

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Lane Profitability</h1>
          <div style={styles.subtitle}>
            Real revenue and profit by lane (origin → destination) -- which lanes are actually worth chasing, and
            which ones look busy but run thin after real costs. Same profit basis as every other report in this app,
            just grouped a different way.
          </div>
        </div>
        <div style={styles.dateRange}>
          <input type="date" value={start} onChange={(e) => handleStartChange(e.target.value)} style={styles.dateInput} max={end} />
          <span style={styles.dateSep}>to</span>
          <input type="date" value={end} onChange={(e) => handleEndChange(e.target.value)} style={styles.dateInput} min={start} max={todayIso()} />
        </div>
      </div>

      {error && <div style={styles.errorBox}>Couldn't load lane profitability: {error}</div>}

      {loading && <LoadingState label="Loading lane profitability…" />}

      {!loading && !error && (
        <>
          <div style={styles.summaryRow}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryValue}>{data?.lanes_count ?? 0}</div>
              <div style={styles.summaryLabel}>Distinct lanes</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryValue}>{fmtMoney(data?.total_revenue_all_lanes)}</div>
              <div style={styles.summaryLabel}>Total revenue, all lanes</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={{ ...styles.summaryValue, color: (data?.total_profit_all_lanes ?? 0) >= 0 ? "#16a34a" : "#dc2626" }}>
                {fmtMoney(data?.total_profit_all_lanes)}
              </div>
              <div style={styles.summaryLabel}>Total profit, all lanes</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryValue}>{fmtRate(data?.avg_price_per_mile_all_lanes)}</div>
              <div style={styles.summaryLabel}>Avg $ / mile, all lanes</div>
            </div>
          </div>

          {lanes.length === 0 && (
            <div style={styles.emptyState}>
              <RouteIcon size={22} color="var(--muted)" />
              <div style={styles.emptyTitle}>No lanes with a real cost record in this range</div>
              <div style={styles.emptySubtitle}>Loads need a saved route plan and a completed trip cost to count here.</div>
            </div>
          )}

          {lanes.length > 0 && (
            <div style={{ ...styles.card, padding: "6px 4px", overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Lane</th>
                    <th style={styles.thNum}>Loads</th>
                    <th style={styles.thNum}>Miles</th>
                    <th style={styles.thNum}>Revenue</th>
                    <th style={styles.thNum}>Profit</th>
                    <th style={styles.thNum}>$/mile</th>
                    <th style={styles.thNum}>Profit/mi</th>
                    <th style={styles.thNum}>Last load</th>
                  </tr>
                </thead>
                <tbody>
                  {lanes.map((l) => (
                    <tr key={`${l.origin}->${l.destination}`}>
                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        {l.origin} <span style={styles.arrow}>→</span> {l.destination}
                      </td>
                      <td style={styles.tdNum}>{l.loads_count}</td>
                      <td style={styles.tdNum}>{Math.round(l.total_miles).toLocaleString("en-US")}</td>
                      <td style={styles.tdNum}>{fmtMoney(l.total_revenue)}</td>
                      <td style={{ ...styles.tdNum, fontWeight: 700, color: (l.total_profit ?? 0) >= 0 ? "#16a34a" : "#dc2626" }}>
                        {fmtMoney(l.total_profit)}
                      </td>
                      <td style={{ ...styles.tdNum, fontWeight: 700, color: cellColor(l.price_per_mile, data?.avg_price_per_mile_all_lanes, true) }}>
                        {fmtRate(l.price_per_mile)}
                      </td>
                      <td style={{ ...styles.tdNum, fontWeight: 700, color: cellColor(l.profit_per_mile, data?.avg_profit_per_mile_all_lanes, true) }}>
                        {fmtRate(l.profit_per_mile)}
                      </td>
                      <td style={styles.tdNum}>{fmtDate(l.most_recent_pickup)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                page={data.page}
                totalPages={data.total_pages}
                totalCount={data.total_count}
                pageSize={data.page_size}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 20, flexWrap: "wrap" },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 640 },
  dateRange: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
  dateInput: {
    fontSize: 12.5, color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "7px 10px",
  },
  dateSep: { fontSize: 12, color: "var(--muted)" },

  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },

  summaryRow: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 },
  summaryCard: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    padding: "16px 18px", boxShadow: "var(--shadow-xs)",
  },
  summaryValue: { fontSize: 20, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 },
  summaryLabel: { fontSize: 12, color: "var(--muted)", marginTop: 2 },

  emptyState: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 6, textAlign: "center",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "40px 20px",
  },
  emptyTitle: { fontSize: 14, fontWeight: 700, color: "var(--ink)" },
  emptySubtitle: { fontSize: 12.5, color: "var(--muted)" },

  card: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-xs)" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th: { textAlign: "left", padding: "10px 14px", fontSize: 10.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4 },
  thNum: { textAlign: "right", padding: "10px 14px", fontSize: 10.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4 },
  td: { padding: "10px 14px", borderTop: "1px solid var(--line)", color: "var(--ink)" },
  tdNum: { textAlign: "right", padding: "10px 14px", borderTop: "1px solid var(--line)", color: "var(--ink-2)", fontVariantNumeric: "tabular-nums" },
  arrow: { color: "var(--muted)", fontWeight: 400 },
};
